import React, { useState } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${BACKEND}${url.startsWith('/') ? '' : '/'}${url}`;
}

// Toast global usado por AskAvailabilityButton — un solo banner flotante compartido
function showCopiedToast() {
  const ID = '__copy_avail_toast';
  document.getElementById(ID)?.remove();
  const t = document.createElement('div');
  t.id = ID;
  t.setAttribute('role', 'status');
  t.style.cssText = 'position:fixed;top:max(env(safe-area-inset-top,0px),16px);left:50%;transform:translateX(-50%);z-index:99999;background:#059669;color:white;padding:12px 18px;border-radius:14px;box-shadow:0 10px 30px rgba(0,0,0,0.25);font-size:13px;font-weight:600;max-width:90vw;text-align:center;line-height:1.4;transition:opacity .3s,transform .3s;opacity:0';
  t.innerHTML = '✅ <b>Información de producto copiada</b><br/><span style="font-weight:500;opacity:.95">Abre el grupo o el vendedor que te atendió y pega el texto para preguntar.</span>';
  document.body.appendChild(t);
  requestAnimationFrame(() => { t.style.opacity = '1'; t.style.transform = 'translateX(-50%) translateY(4px)'; });
  setTimeout(() => {
    t.style.opacity = '0';
    t.style.transform = 'translateX(-50%)';
    setTimeout(() => t.remove(), 350);
  }, 3800);
}

/**
 * Botón "Preguntar disponibilidad" — SOLO copia el texto al portapapeles y
 * muestra un aviso. El vendedor/cliente decide dónde pegarlo (grupo, vendedor
 * personal, WhatsApp Business, etc.).
 */
export function AskAvailabilityButton({ productName, price, sellerPhone, sellerName, sellerSlug }) {
  const [copied, setCopied] = useState(false);

  const priceTxt = (price != null && Number(price) > 0) ? `$${Number(price).toFixed(2)}` : '';
  const text = `¡Hola${sellerName ? ` ${sellerName}` : ''}! ¿Hay disponible "${productName}"${priceTxt ? ` de ${priceTxt}` : ''}?`;

  // Normaliza el teléfono al formato internacional (MX si son 10 dígitos)
  const waPhone = (() => {
    const clean = String(sellerPhone || '').replace(/[^\d+]/g, '');
    if (!clean) return '';
    if (clean.startsWith('+')) return clean.slice(1);
    if (clean.length === 10) return `52${clean}`;
    return clean;
  })();

  const handleClick = async () => {
    // Si hay teléfono del vendedor, abrir WhatsApp directo
    if (waPhone) {
      // Trackear el clic (no bloquear la navegación)
      if (sellerSlug) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/catalog-track`;
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slug: sellerSlug, event: 'wa_click' }),
            keepalive: true,
          }).catch(() => {});
        } catch { /* ignore */ }
      }
      const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank', 'noopener');
      return;
    }
    // Fallback: copiar al portapapeles
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (e) { /* continuar */ }
    setCopied(true);
    showCopiedToast();
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <button
      onClick={handleClick}
      className="w-full text-xs font-semibold py-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition flex items-center justify-center gap-1.5"
      data-testid="ask-availability-btn"
      title={waPhone ? `Abrir WhatsApp de ${sellerName || 'tu vendedor'}` : 'Copia el mensaje para pegar donde tú prefieras'}
    >
      {waPhone ? (
        <>💬 Preguntar por WhatsApp</>
      ) : copied ? (
        <>✅ Copiado</>
      ) : (
        <>💬 Preguntar disponibilidad</>
      )}
    </button>
  );
}

/**
 * Botón "Guardar foto" — descarga la imagen del producto.
 * Usa fetch → blob → object URL para forzar la descarga incluso cuando la
 * imagen es cross-origin (Emergent Object Storage / backend).
 */
export function SavePhotoButton({ imageUrl, filename }) {
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(false);
  const resolved = resolveImage(imageUrl);
  const disabled = !resolved || saving;

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!resolved) return;
    setSaving(true); setOk(false); setErr(false);
    try {
      const res = await fetch(resolved, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      // Derivar extensión del content-type o URL
      const mime = blob.type || '';
      let ext = 'jpg';
      if (mime.includes('png')) ext = 'png';
      else if (mime.includes('webp')) ext = 'webp';
      else if (mime.includes('gif')) ext = 'gif';
      else if (mime.includes('jpeg') || mime.includes('jpg')) ext = 'jpg';
      const safeName = (filename || 'imagen').replace(/[^\w\d\s\-.]/g, '').trim().slice(0, 80) || 'imagen';
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch (e) {
      // Último recurso: abrir en pestaña nueva para que el usuario la guarde manualmente
      try { window.open(resolved, '_blank', 'noopener,noreferrer'); } catch {}
      setErr(true);
      setTimeout(() => setErr(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className="w-full text-xs font-semibold py-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 disabled:bg-slate-50 disabled:text-slate-400 transition flex items-center justify-center gap-1.5"
      data-testid="save-photo-btn"
      title={resolved ? 'Descargar imagen' : 'Sin imagen disponible'}
    >
      {saving ? (
        <>⏳ Guardando…</>
      ) : ok ? (
        <>✅ Guardada</>
      ) : err ? (
        <>⚠️ Abrí la imagen en otra pestaña</>
      ) : (
        <>📸 Guardar foto</>
      )}
    </button>
  );
}
